'use client'

import { formatCoin, formatPrice } from '@polaris/core/utils'
import { useFieldValue, useForm } from 'form-atoms'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { Check, Plus } from 'lucide-react'
import { motion } from 'motion/react'
import { WalletIcon } from '@/src/components/accounts/wallet-icon'
import { useSupportedChainsMap } from '@/src/queries/hooks/chains'
import { usePendingAccountDialog } from '@/src/stores/accounts/detection'
import { pendingAccountsAtom } from '@/src/stores/accounts/wallets'
import {
  openSelectWalletsDialog,
  selectWalletsDialogAtom,
} from '@/src/stores/select-wallets'
import { useTokenFieldActions } from '@/src/stores/swap/actions'
import {
  fromAccountsBalancesAtom,
  fromBalanceAtom,
  isSelectingMultiAccountFromBalanceAtom,
  swapFormAtom,
} from '@/src/stores/swap/atoms'
import { userAtom } from '@/src/stores/user/atoms'
import { cn } from '@/src/utils/styles'
import { WatchAddressIcon } from '../accounts/watch-address'
import { BottomSheet } from '../ui/bottom-sheet'
import {
  BottomSheetListContent,
  BottomSheetListFooter,
  BottomSheetListItem,
  BottomSheetListTitle,
} from '../ui/bottom-sheet/list'
import { Chevron } from '../ui/icons/chevron'
import {
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from '../ui/list-row'

export const FromBalanceSelectPopover = () => {
  const [isOpen, setIsOpen] = useAtom(isSelectingMultiAccountFromBalanceAtom)
  const fromBalance = useAtomValue(fromBalanceAtom)
  const fromAccountsBalances = useAtomValue(fromAccountsBalancesAtom)
  const { accountsWithWallets: pendingAccountsWithWallets } =
    useAtomValue(pendingAccountsAtom)
  const setAddAccountDialog = usePendingAccountDialog()
  const { fieldAtoms } = useForm(swapFormAtom)
  const currentFromValue = useFieldValue(fieldAtoms.from)
  const { setValue: fieldFromSetValue } = useTokenFieldActions({
    direction: 'from',
  })
  const chains = useSupportedChainsMap()
  const chain = currentFromValue?.chain
    ? chains.data?.get(currentFromValue.chain)
    : undefined
  const user = useAtomValue(userAtom)
  const setDialogState = useSetAtom(selectWalletsDialogAtom)

  const handleSelectBalance = (address: string) => {
    if (!currentFromValue) {
      console.error('No from value found to select balance account for')
      return
    }
    if (!currentFromValue.token) {
      console.error('No token found to select balance account for')
      return
    }
    if (!currentFromValue.chain) {
      console.error('No chain found to select balance account for')
      return
    }
    const pendingAccountWithWallet = pendingAccountsWithWallets.find(
      (account) => account.address === address,
    )
    if (pendingAccountWithWallet) {
      setAddAccountDialog(pendingAccountWithWallet.grouping_key)
    } else {
      fieldFromSetValue({
        denom: currentFromValue.token,
        chain_id: currentFromValue.chain,
        address,
      })
    }
    setIsOpen(false)
  }

  return (
    <BottomSheet
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      scrimClassName='bg-scrim-nonmodal'
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className='flex flex-col gap-0 min-h-0 max-h-full'
      >
        <BottomSheetListTitle className='mb-3'>
          {chain ? `Select ${chain.chain_name} balance` : 'Select balance'}
        </BottomSheetListTitle>
        <BottomSheetListContent>
          {fromAccountsBalances
            .filter(
              (account) =>
                !(
                  account.type === 'WatchAddress' &&
                  pendingAccountsWithWallets.some(
                    ({ address }) => account.address === address,
                  )
                ),
            )
            .map(({ balance, ...account }) => (
              <BalanceListRowButton
                key={account.address}
                onClick={() => handleSelectBalance(account.address)}
                isSelected={fromBalance?.address === account.address}
                account={account}
                balance={balance}
                pendingAccountsWithWallets={pendingAccountsWithWallets}
              />
            ))}
          <AddWalletListRowButton
            onClick={() => setDialogState(openSelectWalletsDialog())}
            disabled={!user}
          />
        </BottomSheetListContent>
        <BottomSheetListFooter setIsOpen={setIsOpen} />
      </motion.div>
    </BottomSheet>
  )
}

function BalanceListRowButton({
  onClick,
  isSelected,
  account,
  balance,
  pendingAccountsWithWallets,
}: {
  onClick: () => void
  isSelected: boolean
  account: {
    address: string
    name: string
    type: string
    provider_info?: {
      img: string
      name: string
    }
  }
  balance: {
    value_usd: number
    normalized_amount: number
    ticker: string
  }
  pendingAccountsWithWallets: Array<{ address: string }>
}) {
  return (
    <BottomSheetListItem onClick={onClick}>
      <ListRowSide
        className='flex-row gap-3 items-center w-full'
        containerClassName='w-full'
        showIndicator={false}
      >
        {account.provider_info?.img === 'no-logo' ? (
          <WatchAddressIcon
            className='m-auto rounded-xl'
            disabled={!('wallet' in account)}
          />
        ) : account.provider_info?.img ? (
          <WalletIcon
            logoUrl={account.provider_info.img}
            walletName={account.provider_info.name}
            size={40}
            className={cn('rounded-xl overflow-hidden bg-foreground', {
              'opacity-50': !('wallet' in account),
            })}
          />
        ) : null}
        <ListRowSide containerClassName='flex-1 min-w-0' className='gap-0'>
          <ListRowSideName className='text-base truncate min-w-0'>
            {account.name}
          </ListRowSideName>
          <ListRowSideDescription
            className={cn('leading-6 truncate', {
              'text-tertiary': !('wallet' in account),
              'text-link': pendingAccountsWithWallets.some(
                ({ address }) => account.address === address,
              ),
            })}
          >
            {account.type === 'WatchAddress'
              ? 'Watching'
              : pendingAccountsWithWallets.some(
                    ({ address }) => account.address === address,
                  )
                ? 'Add wallet'
                : 'wallet' in account
                  ? account.provider_info?.name || 'Unknown'
                  : 'Disconnected'}
          </ListRowSideDescription>
        </ListRowSide>
        <ListRowSide
          className='text-right gap-0'
          customIndicator={
            isSelected ? (
              <Check className='text-link size-6' size={24} />
            ) : (
              <div className='size-6' />
            )
          }
          showIndicator
        >
          <ListRowSideName className='text-base font-normal'>
            {formatPrice(balance.value_usd)}
          </ListRowSideName>
          <ListRowSideDescription className='leading-6 whitespace-nowrap'>
            {formatCoin({
              amount: balance.normalized_amount,
              denom: balance.ticker,
            })}
          </ListRowSideDescription>
        </ListRowSide>
      </ListRowSide>
    </BottomSheetListItem>
  )
}

function AddWalletListRowButton({
  onClick,
  disabled,
}: {
  onClick: () => void
  disabled: boolean
}) {
  return (
    <BottomSheetListItem onClick={onClick} disabled={disabled}>
      <div className='flex items-center justify-center size-10 rounded-lg bg-button-secondary shrink-0'>
        <Plus className='text-button-secondary-text group-hover:text-button-secondary-text-hover size-6' />
      </div>
      <ListRowSide containerClassName='flex-1 min-w-0' className='gap-0'>
        <ListRowSideName className='text-base'>
          Add another wallet
        </ListRowSideName>
      </ListRowSide>
      <ListRowSide
        className='text-right gap-0'
        showIndicator
        customIndicator={
          <Chevron className='text-tertiary -rotate-90 size-6' />
        }
      />
    </BottomSheetListItem>
  )
}
